import { useCallback, useEffect, useMemo, useState } from 'react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery as useAppQuery } from 'modules/core/hooks/useQuery'
import { useProductCatalog } from 'modules/it_management/lib/graphqlProductCatalogContext'
import type {
  MarketplaceCart,
  MarketplaceCartLine,
} from 'modules/it_management/lib/marketplaceCartGraphql'
import {
  ADD_MARKETPLACE_CART_LINE,
  CREATE_MARKETPLACE_CART,
  GET_MARKETPLACE_CART,
  REMOVE_MARKETPLACE_CART_LINE,
  UPDATE_MARKETPLACE_CART_LINE,
} from 'modules/it_management/lib/marketplaceCartGraphql'
import { getMarketplaceGraphqlClient } from 'modules/it_management/lib/marketplaceGraphqlClient'
import { PRODUCT_CATALOG_VARIANTS_DOCUMENT } from 'modules/it_management/lib/productCatalogGraphql'

import type { CartItem, OfferType } from './types'

const MARKETPLACE_CART_ID_KEY = 'device_catalog_marketplace_cart_id'

function getCartIdStorageKey(companyId: string, customerId: string): string {
  return `${MARKETPLACE_CART_ID_KEY}_${companyId}_${customerId}`
}

function getCachedCartId(storageKey: string): string | null {
  if (typeof sessionStorage === 'undefined') return null
  const cached = sessionStorage.getItem(storageKey)
  return cached && cached.trim() !== '' ? cached : null
}

function setCachedCartId(storageKey: string, id: string | null): void {
  if (typeof sessionStorage === 'undefined') return
  if (id) {
    sessionStorage.setItem(storageKey, id)
  } else {
    sessionStorage.removeItem(storageKey)
  }
}

function isCartPayload(
  data: unknown
): data is {
  addCartLine?: MarketplaceCart
  updateCartLine?: MarketplaceCart
  removeCartLine?: MarketplaceCart
  createCart?: MarketplaceCart
} {
  return (
    typeof data === 'object' &&
    data !== null &&
    ('addCartLine' in data ||
      'updateCartLine' in data ||
      'removeCartLine' in data ||
      'createCart' in data)
  )
}

function getCartFromMutationData(data: unknown): MarketplaceCart | null {
  if (!isCartPayload(data)) return null
  return data.addCartLine ?? data.updateCartLine ?? data.removeCartLine ?? data.createCart ?? null
}

function isMarketplaceCart(x: unknown): x is MarketplaceCart {
  return (
    typeof x === 'object' &&
    x !== null &&
    'id' in x &&
    'companyId' in x &&
    'customerId' in x &&
    'cartLines' in x
  )
}

function getCartFromCreateData(data: unknown): MarketplaceCart | null {
  if (
    typeof data === 'object' &&
    data !== null &&
    'createCart' in data &&
    data.createCart != null &&
    isMarketplaceCart(data.createCart)
  ) {
    return data.createCart
  }
  return null
}

type VariantOfferForCart = {
  id: string
  type: string
  cost?: number | null
  period?: number | null
}

type VariantDetailsForCart = {
  name: string
  image: string
  offers: VariantOfferForCart[]
}

function mapApiOfferTypeToOfferType(apiType: string): OfferType {
  if (apiType === 'renting' || apiType === 'credit') return apiType
  return 'onetime'
}

function cartLinesToItems(
  lines: MarketplaceCartLine[],
  variantDetails: Map<string, VariantDetailsForCart>
): CartItem[] {
  return lines.map((line) => {
    const details = variantDetails.get(line.productVariantId)
    const offer = details?.offers?.find((o) => o.id === line.productOfferId) ?? null
    const offerType = offer ? mapApiOfferTypeToOfferType(offer.type) : 'onetime'
    const offerPeriod = offer?.period != null && offerType !== 'onetime' ? offer.period : null

    return {
      variantId: line.productVariantId,
      name: details?.name ?? line.productVariantId,
      price: parseFloat(line.unitPrice),
      image: details?.image ?? '',
      quantity: line.quantity,
      addons: [],
      offerId: line.productOfferId,
      offerType,
      offerPeriod,
    }
  })
}

export const CART_UPDATED_EVENT = 'device-catalog-cart-updated'

export function useDeviceCatalogCart() {
  const { company, access } = useCurrentSession()
  const companyId = String(company.id)
  const customerId = String(access.id)
  const storageKey = getCartIdStorageKey(companyId, customerId)

  const [cartId, setCartIdState] = useState<string | null>(() => getCachedCartId(storageKey))
  const [cart, setCart] = useState<MarketplaceCart | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [cartError, setCartError] = useState<string | null>(null)

  useEffect(() => {
    setCartIdState(getCachedCartId(storageKey))
  }, [storageKey])

  const marketplaceClient = getMarketplaceGraphqlClient()
  const { queryContext, buildProductImageUrl } = useProductCatalog()

  const companyIdNum = Number(company.id)

  useEffect(() => {
    if (!cartId || Number.isNaN(companyIdNum)) return
    marketplaceClient
      .query({
        query: GET_MARKETPLACE_CART,
        variables: { id: cartId, companyId: companyIdNum },
      })
      .then((res: { data?: { cart?: MarketplaceCart | null } }) => {
        const fetched = res.data?.cart ?? null
        if (!fetched) {
          setCachedCartId(storageKey, null)
          setCartIdState(null)
          window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT))
        }
        setCart(fetched)
      })
      .catch(() => {
        setCart(null)
      })
  }, [cartId, companyIdNum, marketplaceClient, storageKey])

  const variantIds = useMemo(
    () => cart?.cartLines?.map((l: MarketplaceCartLine) => l.productVariantId) ?? [],
    [cart?.cartLines]
  )

  const { data: variantsData } = useAppQuery(PRODUCT_CATALOG_VARIANTS_DOCUMENT, {
    variables: { ids: variantIds },
    skip: variantIds.length === 0,
    context: queryContext,
  })

  const variantDetailsMap = useMemo(() => {
    const map = new Map<string, VariantDetailsForCart>()
    const nodes = variantsData?.itManagement?.distributorProductVariantsConnection?.nodes ?? []
    for (const node of nodes) {
      if (!node?.id) continue
      const name = node.product?.name ?? node.sku ?? node.id
      const imageUrl = node.firstProductMediaImageUrl ?? undefined
      const offers: VariantOfferForCart[] = (node.offers ?? []).map((o) => ({
        id: o.id,
        type: o.type,
        cost: o.cost ?? null,
        period: o.period ?? null,
      }))
      map.set(node.id, {
        name,
        image: buildProductImageUrl(imageUrl) ?? '',
        offers,
      })
    }
    return map
  }, [variantsData, buildProductImageUrl])

  const items: CartItem[] = useMemo(
    () => (cart ? cartLinesToItems(cart.cartLines, variantDetailsMap) : []),
    [cart, variantDetailsMap]
  )

  const setCartId = useCallback(
    (id: string | null) => {
      setCartIdState(id)
      setCachedCartId(storageKey, id)
      window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT))
    },
    [storageKey]
  )

  const ensureCart = useCallback(async (): Promise<string> => {
    const cached = getCachedCartId(storageKey)
    if (cached) {
      setCartIdState(cached)
      return cached
    }
    const result = await marketplaceClient.mutate({
      mutation: CREATE_MARKETPLACE_CART,
      variables: { companyId: companyIdNum, customerId },
    })
    const newCart = getCartFromCreateData(result.data)
    if (!newCart?.id) {
      throw new Error(result.errors?.[0]?.message ?? 'Failed to create cart')
    }
    setCartId(newCart.id)
    setCart(newCart)
    return newCart.id
  }, [companyIdNum, customerId, marketplaceClient, setCartId, storageKey])

  const addItem = useCallback(
    async (item: CartItem) => {
      setCartError(null)
      try {
        const cid = await ensureCart()
        const line = cart?.cartLines?.find(
          (l: MarketplaceCartLine) =>
            l.productVariantId === item.variantId && l.productOfferId === item.offerId
        )
        const quantity = item.quantity || 1

        if (line) {
          const result = await marketplaceClient.mutate({
            mutation: UPDATE_MARKETPLACE_CART_LINE,
            variables: {
              cartLineId: line.id,
              companyId: companyIdNum,
              quantity: line.quantity + quantity,
            },
          })
          const updated = getCartFromMutationData(result.data)
          if (updated) setCart(updated)
        } else {
          const result = await marketplaceClient.mutate({
            mutation: ADD_MARKETPLACE_CART_LINE,
            variables: {
              cartId: cid,
              companyId: companyIdNum,
              productId: item.productId ?? item.variantId,
              productVariantId: item.variantId,
              productOfferId: item.offerId,
              quantity,
              unitPrice: item.price,
            },
          })
          const updated = getCartFromMutationData(result.data)
          if (updated) setCart(updated)
        }
        setIsOpen(true)
      } catch (err) {
        setCartError(err instanceof Error ? err.message : 'Failed to add to cart')
      }
    },
    [cart, companyIdNum, ensureCart, marketplaceClient]
  )

  const removeItem = useCallback(
    async (variantId: string, offerId: string) => {
      setCartError(null)
      const line = cart?.cartLines?.find(
        (l: MarketplaceCartLine) => l.productVariantId === variantId && l.productOfferId === offerId
      )
      if (!line) return
      try {
        const result = await marketplaceClient.mutate({
          mutation: REMOVE_MARKETPLACE_CART_LINE,
          variables: { cartLineId: line.id, companyId: companyIdNum },
        })
        const updated = getCartFromMutationData(result.data)
        if (updated) setCart(updated)
      } catch (err) {
        setCartError(err instanceof Error ? err.message : 'Failed to remove from cart')
      }
    },
    [cart?.cartLines, companyIdNum, marketplaceClient]
  )

  const updateQuantity = useCallback(
    async (variantId: string, offerId: string, quantity: number) => {
      setCartError(null)
      const clamped = Math.max(0, quantity)
      const line = cart?.cartLines?.find(
        (l: MarketplaceCartLine) => l.productVariantId === variantId && l.productOfferId === offerId
      )
      if (!line) return
      if (clamped === 0) {
        await removeItem(variantId, offerId)
        return
      }
      try {
        const result = await marketplaceClient.mutate({
          mutation: UPDATE_MARKETPLACE_CART_LINE,
          variables: {
            cartLineId: line.id,
            companyId: companyIdNum,
            quantity: clamped,
          },
        })
        const updated = getCartFromMutationData(result.data)
        if (updated) setCart(updated)
      } catch (err) {
        setCartError(err instanceof Error ? err.message : 'Failed to update quantity')
      }
    },
    [cart?.cartLines, companyIdNum, marketplaceClient, removeItem]
  )

  const clearCart = useCallback(() => {
    setCart(null)
    setCartId(null)
    setCartError(null)
  }, [setCartId])

  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [items]
  )
  const itemCount = useMemo(() => items.reduce((sum, item) => sum + item.quantity, 0), [items])
  const total = subtotal

  return {
    items,
    isOpen,
    itemCount,
    subtotal,
    total,
    cartId: cart?.id ?? null,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    openCart: useCallback(() => setIsOpen(true), []),
    closeCart: useCallback(() => setIsOpen(false), []),
    cartError,
  }
}
